import { isPlainObject } from 'es-toolkit';
import { toSearchParams } from './to-search-params';
import type { ClientInput, ClientOptions, CustomRequestInit, TDefaultBody } from './types';
import { ResponseError } from './response-error';

export type InternalClientOptions<TResponse, TBody> = ClientOptions<TResponse, TBody> & {
  fetch?: typeof fetch;
  headers?: Headers;
};

function parseInput(input: string | URL): URL {
  try {
    return new URL(input.toString());
  } catch {
    throw new Error(`Invalid URL: ${input.toString()}`);
  }
}

async function defaultParseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return Promise.resolve('');

  const data = await (response.clone().json() as Promise<unknown>).catch(() => response.text());

  return data ?? null;
}

function isGetRequest<TBody>(requestInit: CustomRequestInit<TBody>): boolean {
  return requestInit.method?.toUpperCase() === 'GET' || !requestInit.method;
}

export const base = async <TResponse = unknown, TBody extends TDefaultBody = TDefaultBody>(
  input: ClientInput,
  options?: InternalClientOptions<TResponse, TBody>
): Promise<TResponse> => {
  const {
    params,
    fetch = globalThis.fetch,
    parseResponse = defaultParseResponse,
    ...requestOptions
  } = options ?? {};

  let { body } = requestOptions;

  const url = parseInput(input);

  if (isGetRequest<TBody>(requestOptions) && params) {
    const searchParams = toSearchParams(params);

    searchParams.forEach((value, key) => {
      url.searchParams.append(key, value);
    });
  }

  if (isPlainObject(body)) {
    body = JSON.stringify(body) as TBody;
  }

  if (typeof body === 'string') {
    requestOptions.headers?.set('Content-Type', 'application/json');
  } else if (body instanceof FormData) {
    // Let the browser set the correct Content-Type with boundary
  } else if (
    body !== undefined &&
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Type guard necessary in case something might slip through.
    body !== null
  ) {
    throw new Error('Body must be a string, object, or FormData');
  }

  const request = new Request(url, {
    credentials: 'include',
    ...requestOptions,
    body,
  });

  const response = await fetch(request);

  if (!response.ok) {
    throw new ResponseError(response, await parseResponse(response), request);
  }

  return parseResponse(response) as Promise<TResponse>;
};
